import type { KbRun, KbRunStatus } from './api';

/**
 * Vocabulary shared by the two surfaces that show ingestion runs — the review queue's Runs tab and a
 * project's Activity section.
 *
 * These lived in both pages, which was survivable while they only had to agree on what "live" meant.
 * The per-project tally below is the part that could not be duplicated: it is the difference between
 * a project page saying what happened *in it* and repeating a number that belongs to three projects.
 */

/** A run in one of these is still moving, so a view showing it polls until it isn't. */
export const LIVE_RUN_STATUSES: KbRunStatus[] = ['QUEUED', 'PROCESSING'];

export function isRunLive(status: KbRunStatus): boolean {
  return LIVE_RUN_STATUSES.includes(status);
}

/** How much of a source something was handed. Rounded past 10k, where the exact figure stops helping. */
export function formatChars(chars: number): string {
  return chars >= 10_000 ? `${Math.round(chars / 1000)}k chars` : `${chars} chars`;
}

export interface RunTally {
  created: number;
  updated: number;
  deleted: number;
  merged: number;
  moved: number;
  flagged: number;
  /**
   * True when these are one project's own numbers, false when they are the run's total across every
   * project it wrote into. A caller that shows this on a project page has to say which it got — the
   * two are the same number for a pinned run and very different for a routed one.
   */
  scoped: boolean;
}

function tallyOf(run: KbRun): RunTally {
  return {
    created: run.nodesCreated,
    updated: run.nodesUpdated,
    deleted: run.nodesDeleted,
    merged: run.nodesMerged,
    moved: run.nodesMoved,
    flagged: run.flaggedForReview,
    scoped: false,
  };
}

/**
 * What a run did, optionally narrowed to one project.
 *
 * The counter columns on a run are its **total**: a routed run that created two pages in one project
 * and five in another reports seven, which is the right number on an organization-wide list and the
 * wrong one on either project's page. Every change it made carries its own project in
 * `result.changes`, so the narrow answer is already on the row — no extra request, which matters for
 * a section that polls.
 *
 * Falls back to the totals unless the change list can be trusted to be complete: its length has to
 * match the counters it is being used in place of. Runs from before per-change projects were recorded
 * have counters and no list, and a partial list would quietly under-report — which on a project page
 * reads as "the agent did nothing here", the one wrong answer worth guarding against.
 */
export function runTallyFor(run: KbRun, projectId?: string): RunTally {
  const total = tallyOf(run);
  const changes = run.result?.changes ?? [];
  if (!projectId || changes.length === 0) return total;

  const counted = total.created + total.updated + total.deleted + total.merged + total.moved;
  if (changes.length !== counted || changes.some((change) => !change.projectId)) return total;

  const mine = changes.filter((change) => change.projectId === projectId);
  const byKind = (kind: string) => mine.filter((change) => change.kind === kind).length;

  return {
    created: byKind('CREATE'),
    updated: byKind('UPDATE'),
    deleted: byKind('DELETE'),
    merged: byKind('MERGE'),
    moved: byKind('MOVE'),
    flagged: mine.filter((change) => change.reviewStatus === 'PENDING').length,
    scoped: true,
  };
}
